import { AbasDeLink, CabecalhoDePagina, CascaArea, Carregando, FaixaAmbiente, FaixaAssistida, Faixas, FaixaVersaoNova, IconeCalendario, IconeCapelo, IconeCarteira, IconeCasa, IconeGrafico, IconeMedalha, IconePessoa, Pilha, Sino, TelaContaConectada, type DestinoDaArea, type PontoDePendencia } from "@mtc/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import { useApi } from "./acoes";
import { config, marca } from "./config";
import { useVersaoDoServidor } from "./versao";
import { PaginaCpfErrado, PaginaDefinirSenha, PaginaEmailErrado, PaginaEntrar, PaginaEsqueciSenha } from "./paginas/Acesso";
import { PaginaEmConstrucao, PaginaInicio } from "./paginas/Logado";
import { PaginaCheckout } from "./paginas/Checkout";
import { PaginaMensalidades } from "./paginas/Mensalidades";
import { PaginaMinhaConta } from "./paginas/MinhaConta";
import { PaginaPlanosAVenda } from "./paginas/PlanosAVenda";
import { PaginaPagar } from "./paginas/Pagar";
import { rotasDosCaminhosAntigos } from "./paginas/LinkAntigo";
import { PaginaRegulamento } from "./paginas/Regulamento";
import { PaginaAcademy, PaginaAula } from "./paginas/Academy";
import { PaginaMercado } from "./paginas/Mercado";
import { PaginaCertificados } from "./paginas/Certificados";
import { PaginaPagamentos } from "./paginas/Pagamentos";
import { PaginaPerfil } from "./paginas/Perfil";
import { PaginaPlanos } from "./paginas/Planos";
import { PaginaRepasses } from "./paginas/Repasses";
import { PaginaSolicitacoes } from "./paginas/Solicitacoes";
import { ProvedorDeAssunto } from "./assunto";
import { PaginaNotificacoes } from "./paginas/Notificacoes";
import { PlanoDaCasca, ProvedorDePlano, usePlanoAtual } from "./planoAtual";
import { useSinalDeUso } from "./uso";
import { useSessao } from "./sessao";
import type { ListaDeAvisos, Resumo } from "./tipos";
import { chaveDaRota, useVisao } from "./visao";

/**
 * Menu por assunto, nao por tela (Vitor, 16/09: "o header esta ficando cheio"). Quatro entradas: o inicio, o plano, o
 * dinheiro e o conteudo. Cada assunto abre com abas, uma por endereco, entao link antigo e e-mail antigo continuam
 * valendo. O que e da pessoa (perfil, certificados) sai do menu e vai pro menu da conta.
 * Cada aba so aparece se o modulo dela existir no ambiente. Nenhum `if` de marca.
 */
type Entrada = { modulos?: string[]; rota: string; rotulo: string; tela?: ReactNode; abaDe?: string; descricao?: string; foraDaBarra?: boolean };
const ENTRADAS: Entrada[] = [
  { rota: "/", rotulo: "Início" },
  // Meu plano
  { rota: "/planos", rotulo: "Situação", tela: <PaginaPlanos />, abaDe: "/planos", descricao: "O que você comprou, onde cada plano está e o que fazer agora." },
  { modulos: ["etapas"], rota: "/etapas", rotulo: "Etapas", tela: <PaginaPlanos modo="etapas" />, abaDe: "/planos", descricao: "Por onde você passou e o que falta pra chegar na Mesa Real." },
  { modulos: ["agendamento"], rota: "/agendamento", rotulo: "Agendamento", tela: <PaginaPlanos modo="agendamento" />, abaDe: "/planos", descricao: "Escolha o dia de início de cada plano. A equipe libera sua conta nessa data." },
  { modulos: ["agendamento", "etapas"], rota: "/solicitacoes", rotulo: "Solicitações", tela: <PaginaSolicitacoes />, abaDe: "/planos", descricao: "Seus pedidos: liberação da plataforma e análise do resultado. O avanço que pede pagamento é pago aqui, por PIX ou cartão ou pelo PIX direto." },
  // A aprovacao do Challenge (spec 064): o mesmo endereco, com o texto do modulo que traz o pedido.
  { modulos: ["nelogica-v2"], rota: "/solicitacoes", rotulo: "Solicitações", tela: <PaginaSolicitacoes />, abaDe: "/planos", descricao: "Seus pedidos de aprovação do Challenge. Quando a aprovação pede a taxa de ativação, você paga aqui, por PIX ou cartão ou pelo PIX direto." },
  // Spec 019, 3.4 (Vitor, 21/09): "Minha assinatura" virou "Minha conta", com a conta na corretora e o saldo. Acende
  // pelo modulo que traz a conta (a Global nao cobra recorrencia; o modulo `assinatura` e esqueleto e vai sair).
  { modulos: ["saldo", "nelogica-v2"], rota: "/minha-conta", rotulo: "Minha conta", tela: <PaginaMinhaConta />, abaDe: "/planos", descricao: "Sua conta na corretora: o número, a etapa e o saldo, sempre com a hora em que foi visto." },
  // Planos a venda (Vitor, 15/10): o ultimo plano comprado primeiro, pra comprar de novo, e os outros da marca.
  { rota: "/comprar", rotulo: "Comprar plano", tela: <PaginaPlanosAVenda />, abaDe: "/planos", descricao: "Os planos à venda agora, com o seu último plano em primeiro pra comprar de novo." },
  // Financeiro
  { modulos: ["mensalidade-manual"], rota: "/mensalidades", rotulo: "Mensalidades", tela: <PaginaMensalidades />, abaDe: "/financeiro", descricao: "A mensalidade de cada plano, a cada 30 dias, paga pelo PIX com o envio do comprovante. Vencida, a área fica bloqueada até um administrador conferir, em até 1 dia depois do pagamento." },
  // A taxa de manutencao da Funded (spec 064) usa os mesmos caminhos da mensalidade, com o nome dela. O valor e o ciclo
  // sao do administrador (spec 065): a frase nao leva numero; o de cada plano vem da API, no cartao de cada um.
  { modulos: ["assinatura"], rota: "/mensalidades", rotulo: "Taxa de manutenção", tela: <PaginaMensalidades />, abaDe: "/financeiro", descricao: "A taxa de manutenção da conta Funded, em reais pela cotação do dólar do dia da cobrança. O valor e a frequência de cada plano estão logo abaixo. Pague por PIX ou cartão, ou pelo PIX direto com o comprovante. Sem o pagamento, o plano é suspenso no dia seguinte ao vencimento." },
  { rota: "/financeiro", rotulo: "Histórico", tela: <PaginaPagamentos />, abaDe: "/financeiro", descricao: "Tudo o que você já pagou aqui: a compra do plano e as mensalidades." },
  { modulos: ["etapas"], rota: "/repasses", rotulo: "Repasses", tela: <PaginaRepasses />, abaDe: "/financeiro", descricao: "O que a equipe pagou a você pelos seus planos." },
  // Conteudo
  { rota: "/academy", rotulo: "Academy", tela: <PaginaAcademy /> },
  // Mercado (spec 056): cotacoes e calendario economico do TradingView, so onde o modulo esta ligado (a Prop).
  // No celular fica fora da barra de baixo (com cinco destinos os rotulos cortavam): o Inicio tem o atalho.
  { modulos: ["mercado"], rota: "/mercado", rotulo: "Mercado", tela: <PaginaMercado />, foraDaBarra: true },
  // Conta (menu do canto, nunca no menu principal)
  { rota: "/certificados", rotulo: "Certificados", tela: <PaginaCertificados />, abaDe: "conta" },
  { rota: "/perfil", rotulo: "Perfil", tela: <PaginaPerfil />, abaDe: "conta" },
];

/** O que o endereco direto de uma parte desligada pelo administrador mostra (spec 060). */
const FORA_DO_AR = "Esta parte da área não está disponível agora";

/** Os assuntos do menu principal, na ordem. O destino e a primeira aba viva do assunto. */
const ASSUNTOS: { chave: string; rotulo: string; icone: ReactNode; pendencia: "meu_plano" | "financeiro" }[] = [
  { chave: "/planos", rotulo: "Meu plano", icone: <IconeGrafico tamanho={24} />, pendencia: "meu_plano" },
  { chave: "/financeiro", rotulo: "Financeiro", icone: <IconeCarteira tamanho={24} />, pendencia: "financeiro" },
];
/** Icone de cada destino solto e de cada item da conta (a rota e a chave). */
const ICONES: Record<string, ReactNode> = {
  "/": <IconeCasa tamanho={24} />,
  "/academy": <IconeCapelo tamanho={24} />,
  "/mercado": <IconeCalendario tamanho={24} />,
  "/perfil": <IconePessoa tamanho={24} />,
  "/certificados": <IconeMedalha tamanho={24} />,
};

/**
 * Pontos de pendencia dos destinos (spec 038, secao 4): GET /v1/me/resumo diz o tom e o motivo. Se a rota falhar, a
 * casca fica sem pontos e segue funcionando. Relê a cada minuto e a cada troca de tela, pra o ponto sumir quando resolve.
 */
function usePendencias(): Record<string, PontoDePendencia | null> {
  const { ler } = useApi();
  const qc = useQueryClient();
  const local = useLocation();
  const q = useQuery({ queryKey: ["resumo"], queryFn: () => ler<Resumo>("/v1/me/resumo"), retry: false, refetchInterval: 60_000 });
  useEffect(() => { void qc.invalidateQueries({ queryKey: ["resumo"] }); }, [local.pathname, qc]);
  const p = q.data?.pendencias;
  if (!p) return {};
  return {
    meu_plano: p.meu_plano?.tom ? { tom: p.meu_plano.tom, motivo: p.meu_plano.motivo } : null,
    financeiro: p.financeiro?.tom ? { tom: p.financeiro.tom, motivo: p.financeiro.motivo } : null,
    "/academy": p.academy?.respostas_novas > 0 ? { tom: "novo", motivo: p.academy.respostas_novas === 1 ? "1 resposta nova da equipe" : `${p.academy.respostas_novas} respostas novas da equipe` } : null,
  };
}

/** "Falar com a equipe": o WhatsApp da marca (config.json) com a mensagem pronta, citando o plano atual. */
function useFalarComEquipe(nome: string | undefined): string | undefined {
  const { atual } = usePlanoAtual();
  const w = marca().whatsapp;
  if (!w) return undefined;
  const quem = nome ? `Sou ${nome.split(" ")[0]}` : "Sou cliente";
  const texto = atual ? `Olá! ${quem}, do plano ${atual.plano.nome}, e preciso de ajuda.` : `Olá! ${quem} e preciso de ajuda.`;
  return `${w}${w.includes("?") ? "&" : "?"}text=${encodeURIComponent(texto)}`;
}

/** Rotas que exigem login. Sem sessao, vai pro /entrar e volta pra onde estava. */
function Protegido({ children }: { children: ReactNode }) {
  const { estado } = useSessao();
  const local = useLocation();
  if (estado.status === "carregando") return <Carregando texto="Conferindo sua sessão" />;
  if (estado.status === "anonimo") return <Navigate to={`/entrar?volta=${encodeURIComponent(local.pathname)}`} replace />;
  return <>{children}</>;
}

/**
 * Rotas publicas de acesso: quem ja esta logado vai direto pro inicio. Menos quando chega de uma compra (`compra=1`) ou
 * por um link de acesso (`token`): a sessao aberta no aparelho pode ser de outra pessoa, entao a area pergunta antes.
 */
function SoAnonimo({ children, perguntar }: { children: ReactNode; perguntar?: "compra" | "link" }) {
  const { estado, sair } = useSessao();
  const [busca] = useSearchParams();
  const navegar = useNavigate();
  const [saindo, setSaindo] = useState(false);
  if (estado.status === "carregando") return <Carregando texto="Conferindo sua sessão" />;
  if (estado.status === "logado") {
    const motivo = perguntar === "compra" && busca.has("compra") ? "compra" : perguntar === "link" && busca.get("token") ? "link" : null;
    if (!motivo) return <Navigate to="/" replace />;
    const trocar = async () => {
      setSaindo(true);
      try { await sair(); } finally { setSaindo(false); }
    };
    return <TelaContaConectada marca={marca()} nome={estado.usuario.nome} motivo={motivo} carregando={saindo} aoContinuar={() => navegar("/", { replace: true })} aoTrocar={() => void trocar()} />;
  }
  return <>{children}</>;
}

/** Sino do trader: busca a cada 30 s; aviso novo tambem atualiza planos e solicitacoes na tela. */
function SinoDoTrader() {
  const { ler, postar } = useApi();
  const qc = useQueryClient();
  const navegar = useNavigate();
  const avisos = useQuery({ queryKey: ["avisos"], queryFn: () => ler<ListaDeAvisos>("/v1/me/notificacoes?limite=20"), refetchInterval: 30_000 });
  const antes = useRef<number | null>(null);
  const naoLidas = avisos.data?.nao_lidas ?? 0;
  useEffect(() => {
    if (antes.current !== null && naoLidas > antes.current) void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== "avisos" });
    antes.current = naoLidas;
  }, [naoLidas, qc]);
  const recarregar = () => qc.invalidateQueries({ queryKey: ["avisos"] });
  return (
    <Sino
      lado="esquerda"
      naoLidas={naoLidas}
      itens={avisos.data?.itens ?? []}
      carregando={avisos.isLoading}
      aoAbrirLista={() => void avisos.refetch()}
      aoAbrirItem={(n) => {
        if (!n.lida) void postar(`/v1/me/notificacoes/${n.id}/lida`).then(recarregar, () => undefined);
        if (n.link?.startsWith("/")) navegar(n.link);
      }}
      aoMarcarTodas={() => void postar("/v1/me/notificacoes/lidas").then(recarregar, () => undefined)}
      verTodasHref="/notificacoes"
    />
  );
}

/** Uma tela dentro de um assunto: o titulo e as abas vem daqui, a tela cuida so do conteudo. */
function Assunto({ titulo, abas, atual, children }: { titulo: string; abas: { rotulo: string; href: string; atual: boolean }[]; atual: Entrada; children: ReactNode }) {
  return (
    <ProvedorDeAssunto value>
      <Pilha espaco={6}>
        <CabecalhoDePagina
          titulo={titulo}
          descricao={atual.descricao}
          abas={abas.length > 1 ? <AbasDeLink rotulo={titulo} abas={abas} /> : undefined}
        />
        {children}
      </Pilha>
    </ProvedorDeAssunto>
  );
}

/** "/assinatura" (o nome antigo da aba) vai pra "/minha-conta" levando a busca junto (`?plano=` escolhe o plano). */
function ParaMinhaConta() {
  const { search } = useLocation();
  return <Navigate to={{ pathname: "/minha-conta", search }} replace />;
}

function AreaLogada() {
  return (
    <ProvedorDePlano>
      <Area />
    </ProvedorDePlano>
  );
}

function Area() {
  const { estado, sair, api } = useSessao();
  const local = useLocation();
  const modulos = new Set(estado.status === "logado" ? estado.usuario.capacidades.modulos : []);
  // Um endereco, uma entrada: quando dois modulos dao a mesma rota (com textos diferentes), vale a primeira da lista.
  const doAmbiente = ENTRADAS.filter((e) => !e.modulos || e.modulos.some((m) => modulos.has(m))).filter((e, i, lista) => lista.findIndex((x) => x.rota === e.rota) === i);
  // Visao do cliente (spec 060): o que o administrador desligou some do menu e das abas; o endereco direto diz que a
  // parte nao esta disponivel, em vez de "nao existe".
  const { mostra } = useVisao();
  const visiveis = doAmbiente.filter((e) => mostra(chaveDaRota(e.rota)));
  const desligadas = doAmbiente.filter((e) => !mostra(chaveDaRota(e.rota)));
  const ativo = (rota: string) => (rota === "/" ? local.pathname === "/" : local.pathname === rota || local.pathname.startsWith(`${rota}/`));
  // A aba Agendamento so aparece com plano esperando o inicio (spec 038); a rota continua valendo pra link antigo.
  const { planos } = usePlanoAtual();
  const temAgendamento = planos.some((m) => m.status === "AGUARDANDO_INICIO");
  const abasDe = (chave: string) => visiveis.filter((e) => e.abaDe === chave && (e.rota !== "/agendamento" || temAgendamento || ativo(e.rota)));
  // Menu principal: o inicio, os assuntos que tem pelo menos uma aba viva, e as telas soltas (Academy).
  const pontos = usePendencias();
  const falar = useFalarComEquipe(estado.status === "logado" ? estado.usuario.nome : undefined);
  const menu: DestinoDaArea[] = [
    { rotulo: "Início", href: "/", atual: ativo("/"), icone: ICONES["/"] },
    ...ASSUNTOS.flatMap((a) => {
      const abas = abasDe(a.chave);
      return abas.length ? [{ rotulo: a.rotulo, href: abas[0]!.rota, atual: abas.some((e) => ativo(e.rota)), icone: a.icone, ponto: pontos[a.pendencia] }] : [];
    }),
    ...visiveis.filter((e) => !e.abaDe && e.rota !== "/").map((e) => ({ rotulo: e.rotulo, href: e.rota, atual: ativo(e.rota), icone: ICONES[e.rota], ponto: pontos[e.rota], foraDaBarra: e.foraDaBarra })),
  ];
  // Perfil primeiro: e o que a pessoa mais procura na conta.
  const conta: DestinoDaArea[] = abasDe("conta").map((e) => ({ rotulo: e.rotulo, href: e.rota, atual: ativo(e.rota), icone: ICONES[e.rota] })).sort((a, b) => (a.href === "/perfil" ? -1 : b.href === "/perfil" ? 1 : 0));
  const assistido = estado.status === "logado" ? estado.usuario.assistido : null;
  // Analise da area (spec 062): o tempo com a aba visivel e em uso. A sessao assistida nao conta.
  useSinalDeUso(estado.status === "logado" && !assistido);
  const encerrarAssistida = async () => {
    await api("/v1/me/acesso-assistido/encerrar", { method: "POST", body: "{}" }).catch(() => undefined);
    window.location.replace("/entrar");
  };
  return (
    <>
    {assistido && estado.status === "logado" && (
      <FaixaAssistida trader={estado.usuario.nome} operador={assistido.operador} expiraEm={assistido.expira_em} aoEncerrar={() => void encerrarAssistida()} />
    )}
    <CascaArea
      plano={(variante) => <PlanoDaCasca variante={variante} />}
      marca={marca()}
      usuario={estado.status === "logado" ? { nome: estado.usuario.nome } : undefined}
      aoSair={() => void sair()}
      sino={<SinoDoTrader />}
      itens={menu}
      conta={conta}
      falarComEquipe={falar}
    >
      <Routes>
        <Route path="/" element={<PaginaInicio />} />
        <Route path="/planos/:id" element={<PaginaPlanos />} />
        <Route path="/academy/aulas/:id" element={mostra("academy") ? <PaginaAula /> : <PaginaEmConstrucao titulo={FORA_DO_AR} />} />
        <Route path="/notificacoes" element={<PaginaNotificacoes />} />
        {visiveis.some((e) => e.rota === "/solicitacoes") && <Route path="/solicitacoes/:id" element={<PaginaSolicitacoes />} />}
        {/* O endereco antigo da aba (spec 019): link salvo ou de e-mail continua chegando na Minha conta. */}
        {visiveis.some((e) => e.rota === "/minha-conta") && <Route path="/assinatura" element={<ParaMinhaConta />} />}
        {visiveis.filter((e) => e.rota !== "/").map((e) => {
          const tela = e.tela ?? <PaginaEmConstrucao titulo={e.rotulo} />;
          const assunto = e.abaDe && e.abaDe !== "conta" ? ASSUNTOS.find((a) => a.chave === e.abaDe) : undefined;
          if (!assunto) return <Route key={e.rota} path={e.rota} element={tela} />;
          const abas = abasDe(assunto.chave).map((x) => ({ rotulo: x.rotulo, href: x.rota, atual: ativo(x.rota) }));
          return <Route key={e.rota} path={e.rota} element={<Assunto titulo={assunto.rotulo} abas={abas} atual={e}>{tela}</Assunto>} />;
        })}
        {desligadas.map((e) => <Route key={e.rota} path={e.rota} element={<PaginaEmConstrucao titulo={FORA_DO_AR} />} />)}
        <Route path="*" element={<PaginaEmConstrucao titulo="Esta tela não existe neste ambiente" />} />
      </Routes>
    </CascaArea>
    </>
  );
}

export function App() {
  const versao = useVersaoDoServidor();
  return (
    <>
      <Faixas>
        {config().AMBIENTE === "homologacao" && <FaixaAmbiente />}
        {versao.desatualizada && <FaixaVersaoNova aoAtualizar={() => window.location.reload()} />}
      </Faixas>
      <Routes>
        <Route path="/entrar" element={<SoAnonimo perguntar="compra"><PaginaEntrar /></SoAnonimo>} />
        <Route path="/suporte/senha" element={<SoAnonimo><PaginaEsqueciSenha /></SoAnonimo>} />
        <Route path="/suporte/email" element={<SoAnonimo><PaginaEmailErrado /></SoAnonimo>} />
        <Route path="/suporte/cpf" element={<PaginaCpfErrado />} />
        <Route path="/acesso/definir-senha" element={<SoAnonimo perguntar="link"><PaginaDefinirSenha /></SoAnonimo>} />
        <Route path="/checkout/:token" element={<PaginaCheckout />} />
        {/* Cobranca (mensalidade, portao do avanco): quem tem o link paga, sem login (spec 003, 4.3). */}
        <Route path="/pagar/:token" element={<PaginaPagar />} />
        {/* O regulamento na versao do aceite (spec 003, decisao 9): publico, e o link que o checkout mostra. */}
        <Route path="/regulamento" element={<PaginaRegulamento />} />
        {/* Caminhos do sistema antigo (spec 009, 4.5): link velho de e-mail e de favorito continua chegando. */}
        {rotasDosCaminhosAntigos()}
        <Route path="/*" element={<Protegido><AreaLogada /></Protegido>} />
      </Routes>
    </>
  );
}
